import { defineComponent as _defineComponent } from 'vue'
import { renderSlot as _renderSlot, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, unref as _unref, createVNode as _createVNode, normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, normalizeStyle as _normalizeStyle, withModifiers as _withModifiers, Transition as _Transition, withCtx as _withCtx, Teleport as _Teleport, createBlock as _createBlock } from "vue"

const _hoisted_1 = ["aria-labelledby"]
const _hoisted_2 = {
  key: 0,
  id: "modal-title",
  class: "ui-modal__title"
}
const _hoisted_3 = {
  key: 1,
  class: "ui-modal__footer"
}

import { Icon } from "@iconify/vue";
import { onMounted, onUnmounted, watch } from "vue";
import type { Size } from "./types";

// Stacked modal counter — only restore scroll when all modals close
interface Props {
  show: boolean;
  title?: string;
  size?: Size | "xxs" | "xl" | "xxl" | "full";
  dialogMaxWidth?: string;
  dialogStyle?: Record<string, string>;
  closeOnBackdrop?: boolean;
  closeOnEscape?: boolean;
  showCloseButton?: boolean;
  blurBackdrop?: boolean;
  transparentBackdrop?: boolean;
  backdropOpacity?: number;
  centered?: boolean;
  headerVariant?: "default" | "blue";
  titleCentered?: boolean;
  contentMaxHeight?: string;
  zIndex?: number;
  bodyClass?: string | string[] | Record<string, boolean>;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'Modal',
  props: {
    show: { type: Boolean },
    title: {},
    size: { default: "md" },
    dialogMaxWidth: {},
    dialogStyle: {},
    closeOnBackdrop: { type: Boolean, default: true },
    closeOnEscape: { type: Boolean, default: true },
    showCloseButton: { type: Boolean, default: true },
    blurBackdrop: { type: Boolean, default: true },
    transparentBackdrop: { type: Boolean, default: false },
    backdropOpacity: { default: undefined },
    centered: { type: Boolean, default: true },
    headerVariant: { default: "default" },
    titleCentered: { type: Boolean, default: false },
    contentMaxHeight: {},
    zIndex: {},
    bodyClass: {}
  },
  emits: ["close", "opened", "closed"],
  setup(__props: any, { emit: __emit }) {

let openModalCount = 0;

const props = __props;

const emit = __emit;

const handleClose = () => {
  emit("close");
};

const handleBackdropClick = () => {
  if (props.closeOnBackdrop) {
    handleClose();
  }
};

const handleEscape = (event: KeyboardEvent) => {
  if (event.key === "Escape" && props.closeOnEscape && props.show) {
    handleClose();
  }
};

watch(
  () => props.show,
  (newValue) => {
    if (newValue) {
      openModalCount++;
      emit("opened");
      if (!props.transparentBackdrop) {
        document.body.style.overflow = "hidden";
      }
    } else {
      openModalCount = Math.max(0, openModalCount - 1);
      emit("closed");
      if (!props.transparentBackdrop && openModalCount === 0) {
        document.body.style.overflow = "";
      }
    }
  }
);

onMounted(() => {
  document.addEventListener("keydown", handleEscape);
});

onUnmounted(() => {
  document.removeEventListener("keydown", handleEscape);
  if (props.show) {
    openModalCount = Math.max(0, openModalCount - 1);
  }
  if (!props.transparentBackdrop && openModalCount === 0) {
    document.body.style.overflow = "";
  }
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_Teleport, { to: "body" }, [
    _createVNode(_Transition, { name: "modal-backdrop" }, {
      default: _withCtx(() => [
        (__props.show)
          ? (_openBlock(), _createElementBlock("div", {
              key: 0,
              class: _normalizeClass(["ui-modal__backdrop", {
          'ui-modal__backdrop--blur': __props.blurBackdrop,
          'ui-modal__backdrop--transparent': __props.transparentBackdrop,
        }]),
              style: _normalizeStyle([
          __props.backdropOpacity != null
            ? { backgroundColor: `rgba(0, 0, 0, ${__props.backdropOpacity})` }
            : undefined,
          __props.zIndex != null ? { zIndex: String(__props.zIndex) } : undefined,
        ]),
              onClick: handleBackdropClick
            }, [
              _createVNode(_Transition, { name: "modal" }, {
                default: _withCtx(() => [
                  (__props.show)
                    ? (_openBlock(), _createElementBlock("div", {
                        key: 0,
                        class: _normalizeClass(["ui-modal__dialog", [
              `ui-modal__dialog--${__props.size}`,
              { 'ui-modal__dialog--centered': __props.centered && !__props.dialogStyle },
            ]]),
                        style: _normalizeStyle([
              __props.dialogMaxWidth ? { maxWidth: __props.dialogMaxWidth } : undefined,
              __props.dialogStyle || undefined,
              __props.zIndex != null ? { zIndex: String(__props.zIndex + 1) } : undefined,
            ]),
                        onClick: _cache[0] || (_cache[0] = _withModifiers(() => {}, ["stop"])),
                        role: "dialog",
                        "aria-modal": "true",
                        "aria-labelledby": __props.title ? 'modal-title' : undefined
                      }, [
                        _createElementVNode("div", {
                          class: _normalizeClass(["ui-modal__content", { 'ui-modal__content--blue-header': __props.headerVariant === 'blue' }]),
                          style: _normalizeStyle(__props.contentMaxHeight ? { maxHeight: __props.contentMaxHeight } : undefined)
                        }, [
                          (__props.title || _ctx.$slots.header)
                            ? (_openBlock(), _createElementBlock("div", {
                                key: 0,
                                class: _normalizeClass(["ui-modal__header", {
                  'ui-modal__header--blue': __props.headerVariant === 'blue',
                  'ui-modal__header--title-centered': __props.titleCentered,
                }])
                              }, [
                                _renderSlot(_ctx.$slots, "header", {}, () => [
                                  (__props.title)
                                    ? (_openBlock(), _createElementBlock("h2", _hoisted_2, _toDisplayString(__props.title), 1))
                                    : _createCommentVNode("", true)
                                ]),
                                (__props.showCloseButton)
                                  ? (_openBlock(), _createElementBlock("button", {
                                      key: 0,
                                      type: "button",
                                      class: "ui-modal__close",
                                      onClick: handleClose,
                                      "aria-label": "Close modal"
                                    }, [
                                      _createVNode(_unref(Icon), { icon: "mdi:close" })
                                    ]))
                                  : _createCommentVNode("", true)
                              ], 2))
                            : _createCommentVNode("", true),
                          _createElementVNode("div", {
                            class: _normalizeClass(["ui-modal__body", __props.bodyClass])
                          }, [
                            _renderSlot(_ctx.$slots, "body"),
                            _renderSlot(_ctx.$slots, "default")
                          ], 2),
                          (_ctx.$slots.footer)
                            ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
                                _renderSlot(_ctx.$slots, "footer")
                              ]))
                            : _createCommentVNode("", true)
                        ], 6)
                      ], 14, _hoisted_1))
                    : _createCommentVNode("", true)
                ]),
                _: 3
              })
            ], 6))
          : _createCommentVNode("", true)
      ]),
      _: 3
    })
  ]))
}
}

})